import type { ResolutionString } from '@charting-library'
import { z } from 'zod'

import { lenientList, moneySchema } from '@/schemas/common'

/**
 * The TradingView chart's shapes, as the datafeed and the save/load adapter
 * hand them to the library.
 *
 * Parsed AFTER `chartMappers` has turned the wire into the library's names
 * (`minmov`, millisecond bar times, numbers for prices), so each schema here
 * describes what the library is promised, not what mm-be sent. Prices are
 * plain numbers for the reason `candlePointSchema` (schemas/market.ts) gives:
 * they are chart coordinates and nothing else reads them.
 *
 * Lenient where mm-be is: a list drops an unreadable row instead of failing
 * the whole window, search or dialog (`lenientList`), and nothing is required
 * that mm-be does not always send.
 */

/**
 * The one zone NEPSE trades in. The library types `timezone` as a closed union
 * of the zones it ships, so the wire's plain string cannot be handed over
 * without an assertion; mm-be sends this same constant for every symbol
 * (services/chart/session.py `TIMEZONE`).
 */
export const NEPSE_TIMEZONE = 'Asia/Kathmandu'

/**
 * The library brands resolutions (`'1D'`, `'W'`) as a nominal string with no
 * runtime constructor, so a string off the wire becomes one only by
 * assertion. This is the one place that assertion is made.
 */
const resolutionSchema = z
  .string()
  .min(1)
  .transform((value) => value as ResolutionString)

/** One daily bar. `time` is epoch MILLISECONDS, the library's unit. */
export const chartBarSchema = z.object({
  time: z.number().int(),
  open: z.number(),
  high: z.number(),
  low: z.number(),
  close: z.number(),
  // Absent, never 0, when mm-be recorded no volume: an index's null turnover
  // is "nobody reported it", which a zero-height bar would misstate.
  volume: z.number().exactOptional(),
})

export type ChartBar = z.infer<typeof chartBarSchema>

/** A `getBars` answer: the bars, oldest first, and the library's metadata. */
export const chartHistorySchema = z.object({
  bars: lenientList(chartBarSchema, 'chart bar'),
  meta: z.object({
    noData: z.boolean(),
    nextTime: z.number().int().exactOptional(),
  }),
})

export type ChartHistory = z.infer<typeof chartHistorySchema>

/**
 * One session's value on a chart pane's line: kitta and counts arrive as integers, rupees and
 * percentages as decimal strings (kept as Money). `null` is a session with nothing recorded, a gap
 * in the line and never 0, and so is a value that will not parse: one bad session must not cost
 * the member the whole pane.
 */
const paneValueSchema = z.union([z.number(), moneySchema]).nullable().catch(null)

export type PaneValue = z.infer<typeof paneValueSchema>

/** One line of a pane: its key, the readable name the legend prints, one value per session. */
const paneLineSchema = z.object({
  key: z.string().min(1),
  label: z.string(),
  values: z.array(paneValueSchema),
})

export type PaneLine = z.infer<typeof paneLineSchema>

/**
 * What a chart pane draws, one value per session: a `/charts/studies/{study}/` read, or the
 * average trade size worked out of `/charts/history/`. `times` are the sessions, epoch SECONDS at
 * 00:00 UTC as mm-be sends them, and every line's `values` is aligned to them.
 */
export const paneSeriesSchema = z.object({
  times: z.array(z.number().int()),
  lines: lenientList(paneLineSchema, 'chart pane line'),
  /** The values are mm-be's estimate (`buy_sell_delta`): NEPSE does not record the side. */
  estimated: z.boolean(),
  /** The asked window held more than mm-be serves; the newest sessions were kept. */
  truncated: z.boolean(),
  /** The newest session in the window (`YYYY-MM-DD`), or null for an empty one. */
  lastSession: z.string().nullable(),
})

export type PaneSeries = z.infer<typeof paneSeriesSchema>

/** `resolveSymbol`'s answer, assignable to the library's `LibrarySymbolInfo`. */
export const chartSymbolSchema = z.object({
  name: z.string().min(1),
  ticker: z.string().min(1),
  description: z.string(),
  type: z.string(),
  session: z.string(),
  exchange: z.string(),
  listed_exchange: z.string(),
  timezone: z.literal(NEPSE_TIMEZONE),
  format: z.literal('price'),
  minmov: z.number(),
  pricescale: z.number(),
  has_intraday: z.boolean(),
  intraday_multipliers: z.array(z.string()),
  has_daily: z.boolean(),
  daily_multipliers: z.array(z.string()),
  has_weekly_and_monthly: z.boolean(),
  supported_resolutions: z.array(resolutionSchema),
  volume_precision: z.number().int(),
  currency_code: z.string(),
  sector: z.string().exactOptional(),
  industry: z.string().exactOptional(),
})

export type ChartSymbol = z.infer<typeof chartSymbolSchema>

/** One symbol-search row, assignable to the library's `SearchSymbolResultItem`. */
const chartSearchResultSchema = z.object({
  symbol: z.string().min(1),
  ticker: z.string().min(1),
  description: z.string(),
  exchange: z.string(),
  type: z.string(),
})

export const chartSearchResultsSchema = lenientList(chartSearchResultSchema, 'chart search')

export type ChartSearchResult = z.infer<typeof chartSearchResultSchema>

/** `onReady`'s answer, assignable to the library's `DatafeedConfiguration`. */
export const chartDatafeedConfigSchema = z.object({
  supports_marks: z.boolean(),
  supports_timescale_marks: z.boolean(),
  supports_time: z.boolean(),
  exchanges: z.array(z.object({ value: z.string(), name: z.string(), desc: z.string() })),
  symbols_types: z.array(z.object({ name: z.string(), value: z.string() })),
  supported_resolutions: z.array(resolutionSchema),
})

export type ChartDatafeedConfig = z.infer<typeof chartDatafeedConfigSchema>

/** A row of the "Load layout" dialog, assignable to the library's `ChartMetaInfo`. */
const chartLayoutMetaSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  symbol: z.string(),
  resolution: resolutionSchema,
  // Epoch SECONDS, as mm-be sends it and the library's dialog reads it.
  timestamp: z.number().int(),
})

export const chartLayoutsSchema = lenientList(chartLayoutMetaSchema, 'chart layout')

export type ChartLayoutMeta = z.infer<typeof chartLayoutMetaSchema>

/** A row of the indicator-templates menu, assignable to `StudyTemplateMetaInfo`. */
const studyTemplateMetaSchema = z.object({ name: z.string().min(1) })

export const studyTemplatesSchema = lenientList(studyTemplateMetaSchema, 'study template')

export type StudyTemplateMeta = z.infer<typeof studyTemplateMetaSchema>

/**
 * A saved layout's or template's `content`: the library's own serialized
 * state, stored by mm-be untouched and handed back as the same string.
 */
export const chartContentSchema = z.string()

/**
 * What lib/warmCache.ts keeps in localStorage between visits: the datafeed
 * config and the symbols charted lately, each stamped with when it was read
 * (epoch ms) so the oldest can be dropped. Read back through these schemas, so
 * a copy written by an older build is ignored rather than handed to the chart.
 */
export const chartWarmCacheSchema = z.object({
  config: chartDatafeedConfigSchema.exactOptional(),
  symbols: z.record(z.string(), z.object({ at: z.number(), info: chartSymbolSchema })),
})

export type ChartWarmCache = z.infer<typeof chartWarmCacheSchema>
